/* ========================================================================
   © 2026 SpeedToLead Framework. All rights reserved.
   ======================================================================== */
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Loader2, ShieldX } from "lucide-react";
import { api } from "@/lib/api";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function ProtectedWorkspace({ children, token }) {
    const location = useLocation();
    const navigate = useNavigate();
    const [authState, setAuthState] = useState(
        location.state?.user ? { phase: "ok", user: location.state.user } : { phase: "checking" },
    );

    useEffect(() => {
        // CRITICAL: If returning from OAuth callback, skip the /me check.
        // AuthCallback will exchange the session_id and establish the session first.
        if (window.location.hash?.includes("session_id=")) {
            return;
        }
        if (authState.phase === "ok") return;
        api.get("/auth/me", { withCredentials: true })
            .then(({ data }) => setAuthState({ phase: "ok", user: data }))
            .catch(() => {
                // Strict isolation: anonymous visitors get bounced to the public
                // funnel immediately. No admin UI, no login screen, no hints.
                // The header "LOG IN" button is the only entry point.
                sessionStorage.setItem(
                    "speedtolead.postLoginRedirect",
                    location.pathname + location.search
                );
                navigate("/", { replace: true });
            });
    }, [authState.phase, location.pathname, location.search, navigate]);

    if (authState.phase === "checking") {
        return (
            <div className="grid min-h-screen place-items-center bg-background text-foreground">
                <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden="true" />
            </div>
        );
    }

    // Authenticated — render children with user object via clone
    return (
        <WorkspaceGate token={token} user={authState.user} onForbidden={() => navigate("/")}>
            {children}
        </WorkspaceGate>
    );
}

function WorkspaceGate({ token, user, children }) {
    // Children component (Workspace) handles its own data fetch via /api/workspace/{token}
    // which returns 403 if user.email doesn't match the profile.email — bubble that to a friendly screen
    const [forbidden, setForbidden] = useState(false);

    useEffect(() => {
        api.get(`/workspace/${token}`, { withCredentials: true }).catch((e) => {
            if (e?.response?.status === 403) setForbidden(true);
        });
    }, [token]);

    if (forbidden) {
        return (
            <div className="min-h-screen bg-background text-foreground">
                <SiteHeader />
                <main role="main" className="mx-auto max-w-2xl px-6 py-20 md:px-10">
                    <section className="border border-primary/40 bg-primary/5 p-10">
                        <ShieldX className="h-6 w-6 text-primary" aria-hidden="true" />
                        <h1 className="mt-3 font-display text-3xl font-bold tracking-tight">
                            This workspace belongs to a different account.
                        </h1>
                        <p className="mt-3 text-sm text-muted-foreground">
                            You&apos;re signed in as <span className="font-mono text-foreground">{user.email}</span>.
                            This workspace was activated under a different email. Sign out and use the email
                            you entered at checkout.
                        </p>
                        <Button
                            data-testid="logout-btn"
                            onClick={async () => {
                                await api.post("/auth/logout", null, { withCredentials: true });
                                window.location.href = "/";
                            }}
                            variant="outline"
                            className="mt-8 rounded-none border-border bg-transparent text-foreground hover:bg-card"
                        >
                            Sign out
                        </Button>
                    </section>
                </main>
                <SiteFooter />
            </div>
        );
    }

    return children;
}
